
import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, Truck, Clock, Star } from 'lucide-react';

interface TrustIndicator {
  icon: React.ReactNode;
  value: string;
  label: string;
  description: string;
}

const trustIndicators: TrustIndicator[] = [
  {
    icon: <Users className="h-6 w-6" />,
    value: "50K+",
    label: "Happy Customers",
    description: "Trusted worldwide"
  },
  {
    icon: <Award className="h-6 w-6" />,
    value: "25+",
    label: "Industry Awards",
    description: "Excellence recognized"
  },
  {
    icon: <Shield className="h-6 w-6" />,
    value: "100%",
    label: "Secure Payment",
    description: "Protected transactions"
  },
  {
    icon: <Truck className="h-6 w-6" />,
    value: "Free",
    label: "Global Shipping",
    description: "Worldwide delivery"
  },
  {
    icon: <Clock className="h-6 w-6" />,
    value: "24/7",
    label: "Support",
    description: "Always available"
  },
  {
    icon: <Star className="h-6 w-6" />,
    value: "4.9★",
    label: "Rating",
    description: "Customer satisfaction"
  }
];

const TrustIndicatorsBar = () => {
  return (
    <section className="py-12 bg-gradient-to-r from-muted/20 via-muted/10 to-muted/20 border-y border-border/50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {trustIndicators.map((indicator, index) => (
            <motion.div
              key={indicator.label}
              className="text-center group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: index * 0.1, 
                duration: 0.6,
                ease: "easeOut"
              }}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              {/* Icon */}
              <motion.div
                className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary rounded-full mb-3 group-hover:bg-primary group-hover:text-white transition-colors duration-300"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                {indicator.icon}
              </motion.div>
              
              {/* Value */}
              <motion.div
                className="text-2xl md:text-3xl font-bold text-foreground mb-1"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  delay: index * 0.1 + 0.3,
                  type: "spring",
                  stiffness: 200
                }}
              >
                {indicator.value}
              </motion.div>
              
              {/* Label */}
              <h4 className="font-semibold text-sm md:text-base text-foreground mb-1">
                {indicator.label}
              </h4>
              
              {/* Description */}
              <p className="text-xs md:text-sm text-muted-foreground leading-tight">
                {indicator.description}
              </p>
              
              {/* Hover effect line */}
              <motion.div
                className="w-0 h-0.5 bg-primary mx-auto mt-2 group-hover:w-full transition-all duration-300"
              />
            </motion.div>
          ))}
        </div>

        {/* Animated background elements */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-32 h-32 bg-primary/5 rounded-full blur-2xl"
              style={{
                left: `${20 + i * 30}%`,
                top: "50%",
              }}
              animate={{
                y: [0, -20, 0],
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: 4 + i,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.8
              }}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TrustIndicatorsBar;
